import { useEffect, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import { SwatchRow } from './SwatchRow';
import { TINTABLE_CATEGORIES, tintFilter } from '../lib/tints';

/**
 * Library picker: fetches /library/manifest.json once, renders category
 * filter chips + a scrollable grid of transparent-BG thumbnails.
 * Clicking a thumb calls onPick(fullSrc, itemName).
 */
export const LibraryPicker = ({ onPick }) => {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [addingSlug, setAddingSlug] = useState(null);
  const [itemTints, setItemTints] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetch('/library/manifest.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setManifest(data);
        if (data?.categories?.length) setActiveCat(data.categories[0].slug);
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div
        data-testid="library-error"
        className="border-2 border-ink bg-peach/50 p-3 text-xs font-work flex items-center gap-2"
      >
        <ImageOff className="h-4 w-4" /> Library unavailable ({error})
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="library-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const cats = manifest.categories || [];
  const cat = cats.find((c) => c.slug === activeCat) || cats[0];

  const tintable = TINTABLE_CATEGORIES.includes(cat?.slug);

  const handlePick = async (item, tint) => {
    if (addingSlug) return;
    setAddingSlug(item.slug);
    try {
      await onPick(item.src, item.name, tint);
    } finally {
      setAddingSlug(null);
    }
  };

  return (
    <div data-testid="library-picker">
      {/* Category filter chips */}
      <div className="flex flex-wrap gap-1 mb-3">
        {cats.map((c) => (
          <button
            key={c.slug}
            data-testid={`library-cat-${c.slug}`}
            type="button"
            onClick={() => setActiveCat(c.slug)}
            className={`border-2 border-ink px-2 py-0.5 text-[10px] font-outfit font-bold uppercase tracking-wider transition-colors ${
              c.slug === activeCat ? 'bg-lilac shadow-brutSm' : 'bg-paper hover:bg-sun/40'
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Item grid */}
      <div className="grid grid-cols-2 gap-2">
        {(cat?.items || []).map((item) => {
          const isAdding = addingSlug === item.slug;
          const tint = tintable ? (itemTints[item.slug] || 'black') : undefined;
          return (
            <div
              key={item.slug}
              className="border-2 border-ink bg-paper shadow-brutSm hover:-translate-y-0.5 transition-transform overflow-hidden"
            >
              <button
                data-testid={`library-item-${item.slug}`}
                type="button"
                disabled={!!addingSlug}
                onClick={() => handlePick(item, tint)}
                className="group relative block w-full disabled:opacity-50 disabled:cursor-wait"
                aria-label={`Add ${item.name}`}
              >
                <div className="aspect-square checker-bg flex items-center justify-center">
                  <img
                    src={item.thumb || item.src}
                    alt={item.name}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain p-1 pointer-events-none"
                    style={tint ? { filter: tintFilter(tint) } : undefined}
                  />
                </div>
                <div className="border-t-2 border-ink px-1.5 py-1 text-left">
                  <span className="font-outfit text-[10px] font-bold uppercase tracking-wider block truncate">
                    {isAdding ? 'Adding…' : item.name}
                  </span>
                </div>
                {isAdding && (
                  <div className="absolute inset-0 flex items-center justify-center bg-paper/70">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
              </button>
              {tintable && (
                <div className="border-t-2 border-ink px-1 py-1.5">
                  <SwatchRow
                    value={tint}
                    onChange={(t) => setItemTints((prev) => ({ ...prev, [item.slug]: t }))}
                    testIdPrefix={`library-item-${item.slug}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="font-work text-[11px] text-ink/60 mt-3 leading-snug">
        {tintable
          ? 'Pick a colour under a glyph, then tap it to drop onto the canvas.'
          : 'Free stickers with transparent backgrounds. Tap to drop onto the canvas.'}
      </p>
    </div>
  );
};
